/**
 * TargetAudiencesTab — section "Publics cibles" de Réglages.
 *
 * Tier 2 catalogue paramétrable (S2.5c). Identique à `EmotionalIntentsTab`
 * mais ciblé sur le profil acheteur. Slug ANGLAIS validé par Claude.
 */

import { useState } from 'react'
import { Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import KebabMenu from '@/components/ui/KebabMenu'
import Modal from '@/components/ui/Modal'
import { FormField } from '@/components/forms/FormField'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useCreateTargetAudience,
  useDeleteTargetAudience,
  useTargetAudiences,
  useUpdateTargetAudience,
} from '@/hooks/useCatalog'
import {
  tier2LabelFormSchema,
  type Tier2LabelFormParsed,
  type Tier2LabelFormValues,
} from '@/schemas/catalog'
import type { TargetAudienceResponse } from '@/types/catalog'

import SlugValidationModal from './SlugValidationModal'

export default function TargetAudiencesTab() {
  const { confirm } = useConfirm()
  const listQuery = useTargetAudiences()
  const createMutation = useCreateTargetAudience()
  const updateMutation = useUpdateTargetAudience()
  const deleteMutation = useDeleteTargetAudience()

  const [editing, setEditing] = useState<TargetAudienceResponse | null>(null)
  const [pendingCreate, setPendingCreate] = useState<Tier2LabelFormParsed | null>(null)
  const [creatingLabel, setCreatingLabel] = useState(false)

  const handleToggleActive = (row: TargetAudienceResponse) => {
    updateMutation.mutate(
      { id: row.id, payload: { active: !row.active } },
      {
        onSuccess: () => {
          toast.success(row.active ? `« ${row.label_fr} » désactivé` : `« ${row.label_fr} » activé`)
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Mise à jour impossible')
        },
      },
    )
  }

  const handleDelete = async (row: TargetAudienceResponse) => {
    const ok = await confirm({
      title: 'Supprimer ce public cible ?',
      message: `« ${row.label_fr} » sera retiré du catalogue. Les œuvres qui le référencent conserveront le slug orphelin — préfère le désactiver.`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(row.id, {
      onError: (err) => {
        toast.error(err.detail ?? 'Suppression impossible')
      },
      onSuccess: () => {
        toast.success(`Public « ${row.label_fr} » supprimé`)
      },
    })
  }

  const handleConfirmSlug = async (slug: string) => {
    if (!pendingCreate) return
    await new Promise<void>((resolve, reject) => {
      createMutation.mutate(
        {
          slug,
          label_fr: pendingCreate.label_fr,
          display_order: pendingCreate.display_order,
        },
        {
          onSuccess: () => {
            toast.success(`Public « ${pendingCreate.label_fr} » ajouté`)
            setPendingCreate(null)
            resolve()
          },
          onError: (err) => {
            toast.error(err.detail ?? 'Création impossible')
            reject(err)
          },
        },
      )
    })
  }

  const rows = listQuery.data ?? []
  const isLoading = listQuery.isLoading

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Publics cibles</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Tier 2 — qui achète l'œuvre. Le slug anglais validé par Claude oriente la voix
            d'écriture des drafts (jeune collectionneur ne résonne pas comme amateur d'art
            contemporain). 1-3 max par œuvre.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setCreatingLabel(true)}>
          <Plus aria-hidden="true" size={16} />
          Ajouter un public
        </Button>
      </header>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title="Aucun public cible"
          description="Ajoute « Amateur de minimalisme », « Jeune collectionneur », « Décorateur d'intérieur »… selon les profils que tu vises."
          action={
            <Button variant="primary" onClick={() => setCreatingLabel(true)}>
              Ajouter le premier public
            </Button>
          }
        />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Libellé</th>
                <th>Slug</th>
                <th>Ordre</th>
                <th>Statut</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-inactive={!row.active || undefined}>
                  <td>
                    <strong>{row.label_fr}</strong>
                  </td>
                  <td>
                    <code className="settings-slug-display">{row.slug}</code>
                  </td>
                  <td className="settings-table-numeric">{row.display_order}</td>
                  <td>
                    {row.active ? (
                      <Badge variant="success">Actif</Badge>
                    ) : (
                      <Badge variant="default">Désactivé</Badge>
                    )}
                  </td>
                  <td className="settings-table-actions">
                    <KebabMenu
                      ariaLabel={`Actions sur ${row.label_fr}`}
                      items={[
                        {
                          id: 'edit',
                          label: 'Modifier',
                          icon: Pencil,
                          onSelect: () => setEditing(row),
                        },
                        {
                          id: 'toggle',
                          label: row.active ? 'Désactiver' : 'Activer',
                          icon: row.active ? PowerOff : Power,
                          onSelect: () => handleToggleActive(row),
                        },
                        {
                          id: 'delete',
                          label: 'Supprimer',
                          icon: Trash2,
                          variant: 'danger',
                          onSelect: () => void handleDelete(row),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creatingLabel ? (
        <LabelModal
          mode="create"
          onClose={() => setCreatingLabel(false)}
          onSubmit={(values) => {
            setPendingCreate(values)
            setCreatingLabel(false)
          }}
        />
      ) : null}

      {pendingCreate ? (
        <SlugValidationModal
          isOpen
          kind="target_audience"
          labelFr={pendingCreate.label_fr}
          onConfirm={handleConfirmSlug}
          onCancel={() => setPendingCreate(null)}
        />
      ) : null}

      {editing ? (
        <LabelModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              updateMutation.mutate(
                {
                  id: editing.id,
                  payload: {
                    label_fr: values.label_fr,
                    display_order: values.display_order,
                  },
                },
                {
                  onSuccess: () => {
                    toast.success(`Public « ${values.label_fr} » mis à jour`)
                    setEditing(null)
                    resolve()
                  },
                  onError: (err) => {
                    toast.error(err.detail ?? 'Mise à jour impossible')
                    reject(err)
                  },
                },
              )
            })
          }
        />
      ) : null}
    </section>
  )
}

// ─── Modal saisie label + display_order (étape 1) ──────────────────────

interface LabelModalProps {
  mode: 'create' | 'edit'
  initial?: TargetAudienceResponse
  onClose: () => void
  onSubmit: (values: Tier2LabelFormParsed) => void | Promise<void>
}

function LabelModal({ mode, initial, onClose, onSubmit }: LabelModalProps) {
  const form = useForm<Tier2LabelFormValues, unknown, Tier2LabelFormParsed>({
    resolver: zodResolver(tier2LabelFormSchema),
    defaultValues: {
      label_fr: initial?.label_fr ?? '',
      display_order: initial?.display_order ?? 0,
    },
  })

  const submitting = form.formState.isSubmitting

  const handleValid = async (values: Tier2LabelFormParsed) => {
    await onSubmit(values)
  }

  const isCreate = mode === 'create'

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isCreate ? 'Nouveau public cible' : `Modifier ${initial?.label_fr}`}
      size="form"
      kicker="Catalogue · Publics"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleValid)} noValidate>
          <Modal.Body className="settings-form-grid">
            <FormField
              name="label_fr"
              label="Libellé français"
              required
              placeholder="Jeune collectionneur, Amateur d'art contemporain…"
              helper={
                isCreate
                  ? "Claude proposera le slug anglais sémantique à l'étape suivante."
                  : 'Le slug est figé après création (référencé par les œuvres).'
              }
              focusOnMount
            />
            {!isCreate && initial && (
              <p className="ui-text-muted ui-body-sm">
                Slug : <code className="settings-slug-display">{initial.slug}</code>
              </p>
            )}
            <FormField name="display_order" label="Ordre d'affichage" type="number" min={0} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" loading={submitting}>
              {isCreate ? 'Continuer · valider le slug' : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}
